import { Button, Modal } from '@sjuni/ui';
import { Pencil, Plus, Trash2 } from 'lucide-react';
import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { ConfirmDialog } from '@/components/ConfirmDialog';
import { FormError } from '@/components/FormError';
import { SubmitButton } from '@/components/SubmitButton';
import { TranslatableField } from '@/components/form/TranslatableField';
import { type Column, ResourceTable } from '@/components/list/ResourceTable';
import { ResourceToolbar } from '@/components/list/ResourceToolbar';
import { useResourceAction, useResourceList, useSaveResource } from '@/lib/api/resource';
import { useAuth } from '@/lib/auth/useAuth';
import { useEditorLocale } from '@/lib/editorLocale/useEditorLocale';
import { applyApiErrors } from '@/lib/forms/applyApiErrors';
import type { TranslatedValue } from '@/lib/forms/translatable';
import { valueFor } from '@/lib/forms/translatable';
import { useListParams } from '@/lib/list/useListParams';

import type { TagInput, TagRow } from './types';

/**
 * docs/05-ADMIN-DASHBOARD.md §2 — `/tags`.
 *
 * The list is sorted by use rather than by name, because after the migration
 * that is the only sort that says anything: several hundred WordPress tags
 * arrive and most of them are on one post each. Alphabetical, the vocabulary in
 * service and the debris are interleaved; by count, they are the top and the
 * bottom of the page.
 *
 * Unlike a category, a tag can always be deleted — `news_tag` is a pivot, so
 * removing one detaches its articles and takes nothing else with it.
 */
export function TagsPage() {
  const { t } = useTranslation();
  const { locale } = useEditorLocale();
  const { can } = useAuth();
  const list = useListParams('-news_count');

  const [editing, setEditing] = useState<TagRow | null | undefined>(undefined);
  const [deleting, setDeleting] = useState<TagRow | null>(null);

  const { data, isPending, isError } = useResourceList<TagRow>('tags', list.params);
  const act = useResourceAction<TagRow>('tags');

  const mayManage = can('news.manage-taxonomy');

  const columns: Column<TagRow>[] = [
    {
      key: 'name',
      label: t('news.tagsPage.columns.name'),
      render: (row) => (
        <span className="font-semibold text-primary">{valueFor(row.name, locale)}</span>
      ),
    },
    {
      key: 'slug',
      label: t('news.categories.columns.slug'),
      render: (row) => (
        <code className="text-caption text-secondary">{valueFor(row.slug, locale)}</code>
      ),
    },
    {
      key: 'translations',
      label: t('news.columns.translations'),
      render: (row) => (
        <span className="text-caption uppercase text-secondary">
          {row.translations_available.join(', ') || '—'}
        </span>
      ),
    },
    {
      key: 'news_count',
      label: t('news.tagsPage.columns.count'),
      sortable: true,
      align: 'end',
      render: (row) => (row.news_count === null ? '—' : row.news_count.toLocaleString()),
    },
    {
      key: 'actions',
      label: t('list.actions'),
      align: 'end',
      render: (row) =>
        mayManage ? (
          <span className="inline-flex gap-1">
            <Button
              type="button"
              size="sm"
              variant="ghost"
              iconLeft={<Pencil size={14} strokeWidth={1.75} aria-hidden="true" />}
              onClick={(event) => {
                event.stopPropagation();
                setEditing(row);
              }}
            >
              {t('news.categories.edit')}
            </Button>

            <Button
              type="button"
              size="sm"
              variant="ghost"
              iconLeft={<Trash2 size={14} strokeWidth={1.75} aria-hidden="true" />}
              onClick={(event) => {
                event.stopPropagation();
                setDeleting(row);
              }}
            >
              {t('common.delete')}
            </Button>
          </span>
        ) : null,
    },
  ];

  return (
    <div className="mx-auto max-w-4xl space-y-6">
      <div>
        <h1 className="text-h2 font-extrabold text-primary">{t('nav.items.tags')}</h1>
        <p className="mt-1 text-body-sm text-secondary">{t('news.tagsPage.intro')}</p>
      </div>

      {isError ? <FormError message={t('errors.generic')} /> : null}

      <ResourceToolbar
        search={list.search}
        onSearchChange={list.setSearch}
        perPage={list.perPage}
        onPerPageChange={list.setPerPage}
        showTrashed={list.showTrashed}
        onShowTrashedChange={list.setShowTrashed}
        meta={data?.meta}
        buildPageHref={list.buildPageHref}
        primaryAction={
          mayManage ? (
            <Button
              type="button"
              size="sm"
              iconLeft={<Plus size={16} strokeWidth={1.75} aria-hidden="true" />}
              onClick={() => setEditing(null)}
            >
              {t('news.tagsPage.add')}
            </Button>
          ) : null
        }
      />

      <ResourceTable
        rows={data?.data ?? []}
        columns={columns}
        rowKey={(row) => row.id}
        loading={isPending}
        sort={list.sort}
        onSortChange={list.setSort}
        onOpen={mayManage ? (row) => setEditing(row) : undefined}
        emptyTitle={t('news.tagsPage.empty.title')}
        emptyBody={t('news.tagsPage.empty.body')}
      />

      {editing !== undefined ? (
        <TagDialog tag={editing} onClose={() => setEditing(undefined)} />
      ) : null}

      <ConfirmDialog
        open={deleting !== null}
        title={t('news.tagsPage.delete.title')}
        body={t('news.tagsPage.delete.body', {
          name: deleting ? valueFor(deleting.name, locale) : '',
          count: deleting?.news_count ?? 0,
        })}
        confirmLabel={t('common.delete')}
        // No `deleted_at` on `tags`, so this one really is final.
        reversible={false}
        busy={act.isPending}
        onCancel={() => setDeleting(null)}
        onConfirm={() => {
          if (deleting) {
            act.mutate(
              { id: deleting.id, action: 'delete' },
              { onSettled: () => setDeleting(null) },
            );
          }
        }}
      />
    </div>
  );
}

interface TagFields {
  name: TranslatedValue;
  slug: TranslatedValue;
}

function TagDialog({ tag, onClose }: { tag: TagRow | null; onClose: () => void }) {
  const { t } = useTranslation();

  const [formError, setFormError] = useState<string | null>(null);

  const save = useSaveResource<TagRow, TagInput>('tags');

  const {
    handleSubmit,
    setError,
    setValue,
    watch,
    formState: { errors, isSubmitting },
  } = useForm<TagFields>({
    defaultValues: { name: tag?.name ?? {}, slug: tag?.slug ?? {} },
  });

  const name = watch('name');

  const onSubmit = handleSubmit(async (values) => {
    setFormError(null);

    try {
      await save.mutateAsync({ id: tag?.id, input: { name: values.name, slug: values.slug } });

      onClose();
    } catch (error) {
      const { formError: message, placed } = applyApiErrors(error, setError, [
        'name',
        'name.ka',
        'slug',
      ]);

      setFormError(message ?? (placed ? null : t('errors.generic')));
    }
  });

  return (
    <Modal
      open
      onClose={onClose}
      title={tag ? t('news.tagsPage.edit') : t('news.tagsPage.add')}
      closeLabel={t('common.close')}
      className="sm:w-[min(92vw,560px)]"
    >
      <form onSubmit={onSubmit} noValidate className="space-y-5">
        <FormError message={formError} />

        <TranslatableField
          label={t('news.tagsPage.columns.name')}
          hint={t('news.tagsPage.nameHint')}
          name="name"
          value={name}
          onChange={(next) => setValue('name', next, { shouldDirty: true })}
          error={errors.name?.message}
        />

        <div className="flex flex-wrap justify-end gap-2">
          <Button type="button" variant="ghost" onClick={onClose} disabled={isSubmitting}>
            {t('common.cancel')}
          </Button>

          <SubmitButton loading={isSubmitting}>{t('editor.publishing.save')}</SubmitButton>
        </div>
      </form>
    </Modal>
  );
}
